if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

      // Meta color swatch (add_products_for_color_options): swap the quick
      // add product in place instead of navigating to the linked product.
      // Fade the old content out, swap, then fade the new content back in.
      this.addEventListener('click', (e) => {
        const swatchLink = e.target.closest(
          '.color-main-meta-option .meta-color-options .redirection-swatch a'
        );
        if (!swatchLink) return;

        const targetUrl = swatchLink.getAttribute('data-color-url');
        if (!targetUrl) return;
        e.preventDefault();

        const swatchLi = swatchLink.closest('.redirection-swatch');
        if (swatchLi.classList.contains('active-color') || this._matatQuickAddSwapping) return;
        this._matatQuickAddSwapping = true;

        const colorValue = swatchLink.getAttribute('data-option-item');
        const swapStart = Date.now();
        const MIN_FADE_MS = 260;
        const contentBox = this.querySelector('.quick-add-modal__content');
        // On the mobile bottom-sheet the modal height is content-driven, so
        // lock it to the old height and ease it to the new one during the
        // hidden phase to avoid a jump.
        const isMobileSheet = window.matchMedia('(max-width: 749px)').matches;
        if (isMobileSheet && contentBox) {
          contentBox.style.height = contentBox.offsetHeight + 'px';
        }

        // Avoid re-triggering the selected-color variant sync of a previous
        // open while the linked product is loading.
        const prevSelected = this.getAttribute('data-selected_vartitle');
        this.setAttribute('data-selected_vartitle', '');

        this.modalContent.classList.add('quickadd-fading');

        this.loadProduct(this._buildSwapUrl(targetUrl))
          .then(() => {
            const renderAt = Date.now();
            // Ease the sheet to the new content height (transition: .3s all).
            if (isMobileSheet && contentBox) {
              contentBox.style.height = contentBox.scrollHeight + 'px';
            }
            // Reveal only once the fade-out and height animation are done, so
            // nothing visibly moves once the fade-in begins. Slide the gallery
            // to the color first (swiper initialized ~100ms after render).
            const revealAt = Math.max(swapStart + MIN_FADE_MS, renderAt + 330);
            setTimeout(() => {
              if (colorValue) quickViewSlideToColor(this.modalContent, colorValue);
              this.modalContent.classList.remove('quickadd-fading');
              if (contentBox) contentBox.scrollTop = 0;
              if (isMobileSheet && contentBox) {
                contentBox.style.height = '';
              }
            }, revealAt - Date.now());
          })
          .catch(() => {
            this.modalContent.classList.remove('quickadd-fading');
            if (isMobileSheet && contentBox) {
              contentBox.style.height = '';
            }
          })
          .finally(() => {
            this.setAttribute('data-selected_vartitle', prevSelected || '');
            this._matatQuickAddSwapping = false;
          });
      });
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      this.modalContent.classList.remove('quickadd-fading');
      this._quickAddLoading(false);

      this.loadProduct(opener.getAttribute('data-product-url'))
        .then(() => {
          super.show(opener);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    _quickAddLoading(show) {
      if (!show && !this._quickAddOverlay) return;
      if (!this._quickAddOverlay) {
        const contentBox = this.querySelector('.quick-add-modal__content');
        if (!contentBox) return;
        const overlay = document.createElement('div');
        overlay.className = 'quickadd-loading-overlay';
        overlay.innerHTML = '<div class="quickadd-loading-spinner"></div>';
        contentBox.appendChild(overlay);
        this._quickAddOverlay = overlay;
      }
      this._quickAddOverlay.classList.toggle('active', !!show);
    }

    loadProduct(url) {
      const cached = this._productCache && this._productCache.get(url);
      const responsePromise = cached
        ? Promise.resolve(cached)
        : fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
              if (!this._productCache) this._productCache = new Map();
              if (this._productCache.size > 20) this._productCache.clear();
              this._productCache.set(url, responseText);
              return responseText;
            });

      return responsePromise.then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this._sectionId = this.productElement.dataset.section || this._sectionId;
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();

          if (typeof initQuickViewSwiper === 'function') {
            setTimeout(() => {
              initQuickViewSwiper(this.modalContent);
              quickViewShowAllImages(this.modalContent);
            }, 100);
          }

          // selected color variants check
          if (this.querySelector('.variation-color-wrapper')) {
            let $current_variant = this.getAttribute('data-selected_vartitle');
            if ($current_variant) {
              this.querySelector('.variation-color-wrapper').querySelectorAll('input').forEach((input) => {
                if ($current_variant.includes(input.value)) {
                  input.checked = true;
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                }
              });
              // Slide the modal gallery to the preselected color once the
              // swiper is ready (initQuickViewSwiper runs ~100ms after render).
              setTimeout(() => {
                if (typeof quickViewSlideToColor === 'function') {
                  quickViewSlideToColor(this.modalContent, $current_variant);
                }
              }, 180);
            }
          }

          this.BISfun();
        });
    }

    _buildSwapUrl(url) {
      if (!this._sectionId) return url;
      const sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + 'section_id=' + encodeURIComponent(this._sectionId);
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }

    BISfun(){
      this.modalContent.querySelectorAll("variant-radios input[type='radio']").forEach((e) => {
        e.addEventListener("change", (event)=> {
            let seletced_option = event.target.classList.contains("disabled");
            if(seletced_option == true){
              this.modalContent.querySelector(".matat-backin-stock-alert").classList.add("active");
              this.modalContent.querySelector(".product-form__submit").classList.add("hidden");
              var outerWrap = this.modalContent.querySelector(".product-form__submit").closest('.product-form-main-container-outer');
              if (outerWrap) outerWrap.classList.add('backinstock-active-main');
              var prodForm = this.modalContent.querySelector(".product-form__submit").closest('product-form');
              if (prodForm) prodForm.classList.add('backinstock-active');
            }else{
              this.modalContent.querySelector(".product-form__submit").classList.remove("hidden");
              this.modalContent.querySelector(".matat-backin-stock-alert").classList.remove("active");
              var outerWrap = this.modalContent.querySelector(".product-form__submit").closest('.product-form-main-container-outer');
              if (outerWrap) outerWrap.classList.remove('backinstock-active-main');
              var prodForm = this.modalContent.querySelector(".product-form__submit").closest('product-form');
              if (prodForm) prodForm.classList.remove('backinstock-active');
            }
        })
      })
      if(this.modalContent.querySelector(".matat-stock-notify-submit")){
        this.modalContent.querySelector(".matat-stock-notify-submit").addEventListener("click", ()=> {
      document.getElementById('phoneValidationMsg').textContent = '';  
      let check_phone = this.validatePhoneNumber();
      if (check_phone) {
                  const button = this.modalContent.querySelector(".matat-stock-notify-submit");
                  const data = {
                      product_id: button.getAttribute("data-product_id"),
                      action: button.getAttribute("data-action"),
                      id: button.getAttribute("data-id"),
                      title: (button.getAttribute("data-title")),
                      price_varies: (button.getAttribute("data-price_varies")),
                      price: (button.getAttribute("data-price")),
                      price_max: (button.getAttribute("data-price_max")),
                      price_min: (button.getAttribute("data-price_min")),
                      has_only_default_variant: (button.getAttribute("data-has_only_default_variant")),
                      src: button.getAttribute("data-src"),
                      url: button.getAttribute("data-url"),
                      variant_id: button.getAttribute("data-variant-id"),
                      phone: document.getElementById("stock-phone").value,
                      user_id: button.getAttribute("data-user_id")
                  };
    
                  fetch('https://shopify-wishlist.matat.io/api/oos', {
                      method: 'POST', 
                      headers: {
                          'Content-Type': 'application/json'
                      },
                      body: JSON.stringify(data)
                  })
                  .then(response => {
                      if (!response.ok) {
                          throw new Error('Network response was not ok');
                      }
    
                  })
                  .then(data => {
                    this.modalContent.querySelector('#phoneValidationMsg').style.color = "green"
                    this.modalContent.querySelector('#phoneValidationMsg').textContent = 'אנו נחזור אליך בהקדם האפשרי, תודה ששלחת';  
                  })
                  .catch(error => {
                      console.error('Error:', error);
                  });
      } else {
        this.modalContent.querySelector('#phoneValidationMsg').textContent = "'מספר טלפון לא תקין.";
      }
      })  
      }
      if(this.modalContent.querySelector('[name="id"]')){
        this.modalContent.querySelector('[name="id"]').addEventListener("change",(e)=>{
          let variant_change_val = e.target.value;
          var notifyBtn = this.modalContent.querySelector(".matat-stock-notify-submit");
          if(notifyBtn){
            notifyBtn.setAttribute("data-variant-id", variant_change_val);
            notifyBtn.setAttribute("data-id", variant_change_val);
            var variantPicker = this.modalContent.querySelector('variant-radios, variant-selects');
            if (variantPicker && typeof variantPicker.getVariantData === 'function') {
              var variantData = variantPicker.getVariantData();
              if (variantData) {
                var matched = variantData.find(function(v) { return v.id == variant_change_val; });
                if (matched) {
                  notifyBtn.setAttribute("data-variant-title", matched.title);
                  if (matched.featured_image) {
                    notifyBtn.setAttribute("data-src", matched.featured_image);
                  }
                }
              }
            }
           }
        })
      }

      this.modalContent.querySelectorAll('variant-radios, variant-selects').forEach((variantPicker) => {
        variantPicker.addEventListener('change', (e) => {
          this.updateQuickViewMedia(variantPicker);
          
          if(e.target.closest('.product-form__input')?.classList.contains('variation-color-wrapper')) {
            quickViewSlideToColor(this.modalContent, e.target.value);
          }
        });
      });
      
    }

    updateQuickViewMedia(variantPicker) {
      const productInfo = variantPicker.closest('.product');
      if (!productInfo) return;

      const mediaList = productInfo.querySelector('.product__media-list');
      if (!mediaList) return;

      const currentVariant = variantPicker.querySelector('[name="id"]')?.value;
      if (!currentVariant) return;

      const variantData = variantPicker.getVariantData?.();
      if (!variantData) return;

      const selectedVariant = variantData.find(v => v.id == currentVariant);
      if (!selectedVariant || !selectedVariant.featured_media) return;

      const targetSrc = selectedVariant.featured_media.preview_image?.src;
      if (!targetSrc) return;

      const currentMedia = mediaList.querySelector('.product__media');
      if (!currentMedia) return;

      const targetMedia = mediaList.querySelector(`img[src*="${targetSrc.split('?')[0]}"]`)?.closest('.product__media');
      if (targetMedia) {
        const targetIndex = Array.from(mediaList.children).indexOf(targetMedia);
        const slider = mediaList.closest('.swiper');
        if (slider && slider.swiper) {
          slider.swiper.slideTo(targetIndex);
        } else {
          const flickity = mediaList.closest('[data-flickity-config]');
          if (flickity && flickity.flickity) {
            flickity.flickity.select(targetIndex);
          }
        }
      }
    }
   validatePhoneNumber() {
      // Get and trim phone number input
      const phoneNumber = document.getElementById('stock-phone').value.trim();

      // Regex pattern for 10-digit phone number
      const pattern = /^\d{10}$/;

      // Validate phone number and update message
      const isValid = pattern.test(phoneNumber);
      return isValid;
    }
  });
}

function initQuickViewSwiper(modalContent) {
  const mediaList = modalContent.querySelector('.product__media-list');
  
  if (!mediaList) return;
  
  const gallery = mediaList.classList.contains('product-slider-gallery') ? mediaList : modalContent.querySelector('.product-slider-gallery');
  const thumbnailSlider = modalContent.querySelector('.thumbnail_slider_side, .thumbnail_slider_bottom');
  
  const swiperWrapper = mediaList.querySelector('.swiper-wrapper');
  if (!swiperWrapper && mediaList.children.length > 0) {
    mediaList.classList.add('swiper');
    const wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';
    while (mediaList.firstChild) {
      wrapper.appendChild(mediaList.firstChild);
    }
    mediaList.appendChild(wrapper);
  }

  if (gallery && !gallery.classList.contains('swiper-initialized')) {
    let mainSwiper, thumbSwiper;

    if (thumbnailSlider) {
      thumbSwiper = new Swiper(thumbnailSlider, {
        spaceBetween: 15,
        slidesPerView: 4,
        watchSlidesProgress: true,
        direction: 'vertical',
        rtl: true,
        on: {
          init: function() {
            thumbnailSlider.classList.add('swiper-initialized');
          }
        }
      });

      mainSwiper = new Swiper(gallery, {
        spaceBetween: 0,
        rtl: true,
        thumbs: { swiper: thumbSwiper },
        navigation: {
          nextEl: gallery.querySelector('.swiper-button-next'),
          prevEl: gallery.querySelector('.swiper-button-prev'),
        },
        on: {
          init: function() {
            gallery.classList.add('swiper-initialized');
          }
        }
      });
    } else {
      mainSwiper = new Swiper(gallery, {
        spaceBetween: 0,
        rtl: true,
        navigation: {
          nextEl: gallery.querySelector('.swiper-button-next'),
          prevEl: gallery.querySelector('.swiper-button-prev'),
        },
        on: {
          init: function() {
            gallery.classList.add('swiper-initialized');
          }
        }
      });
    }

    modalContent.quickViewMainSwiper = mainSwiper;
    modalContent.quickViewThumbSwiper = thumbSwiper;
  }
}

// Normalizes a color name the way normalizeColorName() in global.js does:
// casing, surrounding whitespace and Hebrew niqqud all differ between what
// the admin stores on an option value and what ends up in the image alt.
function normalizeQuickViewColor(str) {
  return (str || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

// A color reaches quickViewSlideToColor in more than one shape: a swatch's
// data-option-item and an option input carry a bare color ("Black"), but the
// card hands over a whole variant title ("Black / M"). The gallery exposes
// only the color in data-media-alt, so fall back to the " / " segments -
// whole string first, so a size can never win a match ahead of the color.
function quickViewColorCandidates(color) {
  const candidates = [];
  const add = (value) => {
    const normalized = normalizeQuickViewColor(value);
    if (normalized && candidates.indexOf(normalized) === -1) candidates.push(normalized);
  };

  add(color);
  (color || '').toString().split(' / ').forEach(add);

  return candidates;
}

function quickViewSlideToColor(modalContent, color) {
  if (!color) return;

  const mainWrapper = modalContent.querySelector('.product__media-list .swiper-wrapper');

  if (!mainWrapper) return;

  const allSlides = Array.from(modalContent.querySelectorAll('#all-product-slides .product__media-item'));

  let matchedSlides = [];
  quickViewColorCandidates(color).some((candidate) => {
    matchedSlides = allSlides.filter(
      (slide) => normalizeQuickViewColor(slide.getAttribute('data-media-alt')) === candidate
    );
    return matchedSlides.length > 0;
  });

  // Photos whose alt names no color (an empty data-media-alt, such as group
  // shots - see media-alt-color.liquid) stay with every color, after the
  // color's own photos. A product whose alt text names no color keeps its
  // full gallery.
  const sharedSlides = allSlides.filter(
    (slide) => normalizeQuickViewColor(slide.getAttribute('data-media-alt')) === ''
  );
  const slidesToShow = matchedSlides.length ? matchedSlides.concat(sharedSlides) : allSlides;

  mainWrapper.innerHTML = '';
  slidesToShow.forEach(slide => {
    const clone = slide.cloneNode(true);
    clone.classList.add('swiper-slide');
    mainWrapper.appendChild(clone);
  });

  if (modalContent.quickViewMainSwiper) {
    modalContent.quickViewMainSwiper.update();
    modalContent.quickViewMainSwiper.slideTo(0);
  }
}

function quickViewShowAllImages(modalContent) {
  const mainWrapper = modalContent.querySelector('.product__media-list .swiper-wrapper');
  if (!mainWrapper) return;
  
  const allSlides = modalContent.querySelectorAll('#all-product-slides .product__media-item');
  if (allSlides.length === 0) return;
  
  mainWrapper.innerHTML = '';
  
  allSlides.forEach(slide => {
    const clone = slide.cloneNode(true);
    clone.classList.add('swiper-slide');
    mainWrapper.appendChild(clone);
  });

  if (modalContent.quickViewMainSwiper) {
    modalContent.quickViewMainSwiper.update();
    modalContent.quickViewMainSwiper.slideTo(0);
  }
}
